import {
  convertDateOfBirth,
  convertSexType,
} from '@lmt/shared-types/src/egk-card/PatientEgkCardData';
import { mockEgkCardReading } from '@lmt/unicorn-shared/app/components/card-reader/utils/mock-card-reading';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { DeveloperMode } from '@lmt/unicorn-shared/app/components/service/DeveloperMode';
import { ToolBarIconButton } from '@lmt/unicorn-shared/app/components/toolbar/components/ToolBarIconButton';
import { useIsSuperUser } from '@lmt/unicorn-shared/app/components/users/superuser/utils/useIsSuperUser';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { useGenerateDataDialog } from '@lmt/unicorn-shared/app/helpers/generate-data-dialog';
import {
  devLabel,
  isDevVersion,
} from '@lmt/unicorn-shared/app/helpers/version-helper';
import AddIcon from '@lmt/unicorn-shared/app/icons/toolbar-icons/add.svg?react';
import ImportIcon from '@lmt/unicorn-shared/app/icons/toolbar-icons/import.svg?react';
import settingsIcon from '@lmt/unicorn-shared/app/icons/settings.svg?react';
import helpIcon from '@lmt/unicorn-shared/app/icons/help.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useEventCallback } from '@mui/material';
import queryString from 'query-string';
import React, { ChangeEvent, FC, useRef } from 'react';
import {
  PatientSearch,
  SearchInputPatientData,
} from '../common/patient/PatientSearch';
import { useGlobalForms } from '../global-forms/useGlobalForms';
import { ToolBarUserProfile } from './components/ToolBarUserProfile';
import {
  ToolBarButton,
  ToolBarComponents,
  ToolBarContainer,
  ToolBarText,
} from './ToolBarComponents';
import styles from './ToolBar.module.scss';
import { PatientEgkCardData } from '@lmt/shared-types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import LogoTextIcon from '@lmt/unicorn-shared/app/icons/logo-title.svg?react';
import { APP_VERSION } from '@lmt/unicorn-shared/app/constants/constants';
import clsx from 'clsx';
import { NightThemeToggle } from '../workstation-settings/components/NightThemeToggle';
import { RequestPatientButton } from './components/RequestPatientButton';
import { DialogTestScenariosButton } from './components/DialogTestScenariosButton';
import { readFileAsString } from '../../helpers/file-helper';
import { isElectron } from '../../helpers/common';
import { openFileByPath } from '../../helpers/download-files';
import { getCurrentCulture } from '../../constants/languages';

const Logo = () => (
  <div className={styles.logo} data-test-id="main-logo" title={APP_VERSION}>
    <LogoTextIcon className={styles.logoText} />
  </div>
);

export let WindowControls: JSX.Element;

export function setWindowControls(controls: JSX.Element) {
  WindowControls = controls;
}

export const UniToolBar: FC = () => {
  const forms = useGlobalForms();
  const i18n = useScopedTranslation('toolbar');
  const claims = useClaims();
  const tabs = useTabs();

  const culture = getCurrentCulture();

  const isSuperUser = useIsSuperUser();
  const mockCardReadingRef = useRef<HTMLInputElement>(null);
  const openPatientProfile = useEventCallback(
    (patient: SearchInputPatientData | null) =>
      patient &&
      tabs.openTab(pages.patientProfile.link({ patientId: patient.id })),
  );

  const openExtendedSearchPage = (searchText: string) => {
    tabs.openTab(
      `${pages.patientSearch.link()}`,
      queryString.stringify({
        search: searchText ?? undefined,
      }),
    );
  };

  const generateData = useGenerateDataDialog();

  const readFileWithEgkData = useEventCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target!.files![0];
      if (!file) {
        return;
      }
      const fileData = await readFileAsString(file, 'UTF-8');

      try {
        if (fileData) {
          const rawData: any = JSON.parse(fileData);
          const data: PatientEgkCardData = {
            ...rawData,
            dateOfBirth: convertDateOfBirth(rawData.dateOfBirth),
            sex: convertSexType(rawData.sex),
          };
          mockEgkCardReading(data);
        }
      } catch (e) {
        console.error('readFileWithEgkData', e);
      } finally {
        // to allow choosing the same file again
        mockCardReadingRef.current!.value = '';
      }
    },
  );

  if (isSuperUser) {
    return (
      <ToolBarComponents>
        <ToolBarContainer>
          <Logo />
        </ToolBarContainer>
        <ToolBarContainer>
          <ToolBarUserProfile />
          {!!WindowControls && (
            <div className={styles.controls}>{WindowControls}</div>
          )}
        </ToolBarContainer>
      </ToolBarComponents>
    );
  }
  return (
    <ToolBarComponents>
      <ToolBarContainer>
        <Logo />
        <ToolBarText>{i18n.t('patient')}</ToolBarText>
        <ToolBarButton
          icon={AddIcon}
          title={i18n.t('add_patient')}
          onClick={() => forms.openPatientCreating()}
          testId="toolbar-addPatient"
        />
        <RequestPatientButton />
        {claims.permissions.manualMeasurementImporting &&
          claims.permissions.importMeasurements && (
            <ToolBarButton
              icon={ImportIcon}
              title={i18n.t('import')}
              onClick={() => forms.importMeasurementFiles()}
              testId="toolbar-import"
            />
          )}
        {claims.permissions.mockEgkCardReader && (
          <>
            <ToolBarButton
              icon={ImportIcon}
              title={'Read test eGK card'}
              onClick={() => mockCardReadingRef.current!.click()}
              testId="toolbar-read-egk-card"
            />
            <input
              type="file"
              accept=".json"
              id="mockEgkCardReading"
              ref={mockCardReadingRef}
              className={'hidden'}
              onChange={readFileWithEgkData}
            />
          </>
        )}
        <ToolBarText>{i18n.t('visit')}</ToolBarText>
        <ToolBarButton
          icon={AddIcon}
          title={i18n.t('add_visit')}
          onClick={() => forms.openVisitCreating()}
          testId="toolbar-addVisit"
        />
        <DeveloperMode>
          <ToolBarText>{i18n.t('developer')}</ToolBarText>
          <ToolBarButton
            icon={ImportIcon}
            title={'Import .gdt file'}
            onClick={() => forms.importGDTFile()}
            testId="toolbar-import-gdt"
          />
          <ToolBarButton
            icon={ImportIcon}
            title={i18n.t('import_pts')}
            onClick={() => forms.importPtsFiles()}
          />
          <ToolBarButton
            icon={AddIcon}
            title={'Generate test data'}
            onClick={generateData}
            testId="toolbar-generateTestData"
          />
          <DialogTestScenariosButton />
          <NightThemeToggle isToollbar={true} />
        </DeveloperMode>
      </ToolBarContainer>
      <ToolBarContainer>
        <div
          className={clsx(
            !!WindowControls && styles.emptyPlaceToDragWindowOnDesktop,
          )}
        />
      </ToolBarContainer>
      <div
        className={clsx(
          !!WindowControls && styles.emptyPlaceToDragWindowOnDesktop,
        )}
      />
      <ToolBarContainer>
        {isDevVersion(APP_VERSION) && (
          <div className={styles.devMode}>{devLabel}</div>
        )}
        <PatientSearch
          className={styles.patientSearch}
          isOnlyActive={false}
          isDeleted={true}
          onSelectedPatientChanged={openPatientProfile}
          onOpenExtendedSearchPage={openExtendedSearchPage}
          resetAfterSelect={true}
          testId={'globalSearchField'}
          selectPatientIfOnlyOneFound={true}
        />
        <ToolBarIconButton
          title={i18n.t('user_manuals')}
          icon={helpIcon}
          onClick={async () => {
            if (isElectron()) {
              await openFileByPath(`help-info/${culture}/index.html`);
            } else {
              const url = `/help-info/${culture}/index.html`;
              window.open(url);
            }
          }}
          testId="user-manuals-button"
        />
        <ToolBarIconButton
          icon={settingsIcon}
          onClick={() => {
            tabs.openTab(pages.settings.link());
          }}
          testId="toolbarSettings"
        />
        <ToolBarUserProfile />
        {!!WindowControls && (
          <div className={styles.controls}>{WindowControls}</div>
        )}
      </ToolBarContainer>
    </ToolBarComponents>
  );
};
